import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NavBar } from "@/components/NavBar";
import { api, apiError, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProofField } from "@/components/ProofField";
import { QuestStepsDialog } from "@/components/QuestStepsDialog";
import { toast } from "sonner";
import {
  ExternalLink, Lock, CheckCircle2, Clock, XCircle, Radar, Upload, ShieldCheck, Gift, Sparkles,
  ShoppingBag, Utensils, Share2, Video, Gamepad2, Crosshair, Dice5, Layers, Check, Radio,
} from "lucide-react";

const CATEGORIES = {
  retail: { label: "Retail", icon: ShoppingBag },
  dining: { label: "Dining", icon: Utensils },
  social: { label: "Social", icon: Share2 },
  video: { label: "Video", icon: Video },
  gaming: { label: "Gaming", icon: Gamepad2 },
  ingame: { label: "In-game", icon: Crosshair },
  betting: { label: "Betting", icon: Dice5 },
  other: { label: "Other", icon: Layers },
};

const CADENCE_LABEL = { once: "One time", daily: "Daily", weekly: "Weekly", monthly: "Monthly" };

const STATUS = {
  available: { label: "Available", cls: "border-primary/40 text-primary", icon: Radar },
  started: { label: "In progress", cls: "border-amber-400/50 text-amber-300", icon: Radar },
  pending_review: { label: "In review", cls: "border-purple-400/50 text-purple-300", icon: Clock },
  approved: { label: "Completed", cls: "border-emerald-400/50 text-emerald-300", icon: CheckCircle2 },
  rejected: { label: "Try again", cls: "border-red-400/50 text-red-300", icon: XCircle },
  locked: { label: "Locked", cls: "border-border text-muted-foreground", icon: Lock },
};

const cardState = (q) => (q.locked ? "locked" : q.attempt?.status || "available");

const StatusPill = ({ status, testid }) => {
  const s = STATUS[status] || STATUS.available;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 border px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] ${s.cls}`}
      data-testid={testid}
    >
      <Icon className="h-2.5 w-2.5" /> {s.label}
    </span>
  );
};

const BrandAvatar = ({ quest, size = "h-10 w-10" }) => {
  const [broken, setBroken] = useState(false);
  const src = quest.brand_logo;
  const initial = (quest.brand_name || quest.title || "?").trim().charAt(0).toUpperCase();
  return (
    <div className={`flex ${size} shrink-0 items-center justify-center overflow-hidden rounded-full border border-primary/30 bg-secondary`}>
      {src && !broken ? (
        <img src={src} alt={quest.brand_name} className="h-full w-full object-cover"
          onError={() => setBroken(true)} />
      ) : (
        <span className="mono text-sm font-semibold text-primary">{initial}</span>
      )}
    </div>
  );
};

const QuestCard = ({ q, onOpen }) => {
  const cat = CATEGORIES[q.category] || CATEGORIES.other;
  const CatIcon = cat.icon;
  const a = q.attempt;
  return (
    <button
      type="button"
      onClick={() => onOpen(q)}
      className="card-hover flex h-full flex-col border border-border bg-card p-4 text-left transition-colors hover:border-primary/60"
      data-testid={`quest-card-${q.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <BrandAvatar quest={q} />
        <StatusPill status={cardState(q)} testid={`quest-status-${q.id}`} />
      </div>

      <div className="mt-3 flex-1">
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
          {q.brand_name}
          {q.sponsored ? (
            <span className="flex items-center gap-1 text-primary" data-testid={`quest-sponsored-${q.id}`}>
              <Sparkles className="h-2.5 w-2.5" /> Sponsored quest
            </span>
          ) : (
            <span className="text-foreground/60" data-testid={`quest-house-${q.id}`}>· House quest</span>
          )}
        </div>
        <h3 className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-snug">
          {q.title}
        </h3>
        <p className="mt-1 line-clamp-2 min-h-[2rem] text-[11px] leading-relaxed text-muted-foreground">
          {q.description}
        </p>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="mono border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
          +{fmt(q.points)}
        </span>
        {q.offer_text && (
          <span
            className="line-clamp-1 border border-emerald-400/30 bg-emerald-400/10 px-1.5 py-0.5 text-[10px] text-emerald-300"
            title={q.offer_text}
            data-testid={`quest-offer-${q.id}`}
          >
            {q.offer_text}
          </span>
        )}
      </div>

      {q.tracking === "watch" ? (
        <div className="mt-3" data-testid={`quest-watch-progress-${q.id}`}>
          <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
            <span>Watch time</span>
            <span className="mono">
              {Math.min(q.watch_progress || 0, q.watch_minutes)} / {q.watch_minutes} min
            </span>
          </div>
          <div className="mt-1 h-0.5 w-full bg-border">
            <div className="h-full bg-primary"
              style={{ width: `${Math.min(100, ((q.watch_progress || 0) / q.watch_minutes) * 100)}%` }} />
          </div>
        </div>
      ) : q.steps?.length > 0 ? (
        <div className="mt-3 flex gap-1" data-testid={`quest-steps-progress-${q.id}`}>
          {q.steps.map((s, i) => {
            const st = a?.steps?.[s.key]?.status;
            const cls =
              st === "approved" ? "bg-primary"
                : st === "pending_review" ? "bg-purple-400"
                : st === "rejected" ? "bg-red-400" : "bg-border";
            return <div key={s.key} className={`h-0.5 flex-1 ${cls}`} title={`Step ${i + 1}: ${s.label}`} />;
          })}
        </div>
      ) : (
        <div className="mt-3 h-0.5" aria-hidden />
      )}

      <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-2 text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <CatIcon className="h-2.5 w-2.5" /> {cat.label}
        </span>
        <span>{CADENCE_LABEL[q.cadence] || q.cadence}</span>
      </div>
    </button>
  );
};

const QuestDetail = ({ q, onClose, onStart, onSteps, onProof }) => {
  const a = q.attempt;
  const done = a?.status === "approved";
  const cat = CATEGORIES[q.category] || CATEGORIES.other;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-border bg-card sm:max-w-lg"
        data-testid="quest-detail-dialog">
        <DialogHeader>
          <DialogTitle className="pr-6 text-lg leading-snug">{q.title}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BrandAvatar quest={q} size="h-11 w-11" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {q.brand_name}
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                {q.sponsored ? <span className="text-primary">Sponsored quest</span> : <span>House quest</span>}
                <span>{cat.label}</span>
                <span>{CADENCE_LABEL[q.cadence] || q.cadence}</span>
              </div>
            </div>
          </div>
          <StatusPill status={cardState(q)} testid={`quest-detail-status-${q.id}`} />
        </div>

        {q.image_url && (
          <img src={q.image_url} alt={q.title} className="h-40 w-full border border-border object-cover" />
        )}

        {q.description && <p className="text-sm leading-relaxed text-muted-foreground">{q.description}</p>}

        {q.offer_text && (
          <div className="border-l-2 border-emerald-400 bg-emerald-400/5 px-3 py-2 text-xs text-emerald-200"
            data-testid="quest-detail-offer">
            {q.offer_text}
          </div>
        )}

        <div className="flex items-center justify-between border border-border bg-secondary/40 px-4 py-3">
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Points on offer
          </span>
          <span className="mono text-xl font-semibold text-primary">+{fmt(q.points)}</span>
        </div>

        {q.tracking === "watch" && (
          <div className="border border-border bg-secondary/40 px-4 py-3" data-testid="quest-detail-watch">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Radio className="h-3 w-3 text-primary" /> Stream time
              </span>
              <span className="mono">
                {Math.min(q.watch_progress || 0, q.watch_minutes)} / {q.watch_minutes} min
              </span>
            </div>
            <div className="mt-2 h-1 w-full bg-border">
              <div className="h-full bg-primary"
                style={{ width: `${Math.min(100, ((q.watch_progress || 0) / q.watch_minutes) * 100)}%` }} />
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Watch a live LiquidRP creator on the Watch page — your minutes count automatically and
              the points land the moment you hit {q.watch_minutes} minutes.
            </p>
          </div>
        )}

        {q.steps?.length > 0 && (
          <div className="space-y-2" data-testid="quest-detail-steps">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {q.steps.length} steps · {a?.steps_approved || 0} approved
            </div>
            {q.steps.map((s, i) => {
              const st = a?.steps?.[s.key]?.status;
              return (
                <div key={s.key} className="flex items-start gap-3 border border-border/60 px-3 py-2">
                  <span className={`mono mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border text-[10px] ${
                    st === "approved" ? "border-primary text-primary" : "border-border text-muted-foreground"
                  }`}>
                    {st === "approved" ? <Check className="h-3 w-3" /> : i + 1}
                  </span>
                  <div className="flex-1">
                    <div className="text-xs font-medium">{s.label}</div>
                    {s.instruction && (
                      <div className="mt-0.5 text-[11px] text-muted-foreground">{s.instruction}</div>
                    )}
                  </div>
                  <span className="mono text-[11px] text-primary">+{fmt(s.points)}</span>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
          <span data-testid={`quest-verify-${q.id}`}>
            <span className="text-foreground/80">{q.verification}</span> — {q.verification_hint}
          </span>
        </div>

        {a?.status === "rejected" && a.reject_reason && (
          <p className="text-xs text-red-400">Staff note: {a.reject_reason}</p>
        )}

        <div className="flex items-center gap-2">
          {q.locked ? (
            <Button disabled className="w-full rounded-none" data-testid={`quest-locked-${q.id}`}>
              <Lock className="mr-2 h-4 w-4" /> Level {q.min_level} required
            </Button>
          ) : done ? (
            <Button disabled className="w-full rounded-none" data-testid={`quest-done-${q.id}`}>
              <CheckCircle2 className="mr-2 h-4 w-4" /> Completed
            </Button>
          ) : q.tracking === "watch" ? (
            <Button asChild className="w-full rounded-none" data-testid={`quest-watch-cta-${q.id}`}>
              <Link to="/watch">
                <Radio className="mr-2 h-4 w-4" /> {q.cta_label || "Go watch"}
              </Link>
            </Button>
          ) : (
            <>
              <Button
                className="w-full rounded-none"
                onClick={() => (a && q.steps?.length ? onSteps(q) : onStart(q))}
                data-testid={`quest-start-${q.id}`}
              >
                {q.has_link && !a && <ExternalLink className="mr-2 h-4 w-4" />}
                {a ? (q.steps?.length ? "Continue" : "Open again") : q.cta_label}
              </Button>
              {q.steps?.length > 0 && a && q.has_link && (
                <Button variant="outline" className="shrink-0 rounded-none"
                  onClick={() => window.open(a.tracking_url, "_blank", "noopener")}
                  data-testid={`quest-link-${q.id}`}>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
              {q.tracking === "proof" && !q.steps?.length && (
                <Button variant="outline" className="shrink-0 rounded-none"
                  onClick={() => onProof(q)} data-testid={`quest-proof-${q.id}`}>
                  <Upload className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Quests() {
  const { user, setUser } = useAuth();
  const [quests, setQuests] = useState([]);
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState("all");
  const [detailFor, setDetailFor] = useState(null);
  const [proofFor, setProofFor] = useState(null);
  const [stepsFor, setStepsFor] = useState(null);
  const [proof, setProof] = useState({ url: "", kind: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [q, h, s] = await Promise.all([
      api.get("/quests"),
      api.get("/me/quests"),
      api.get("/me/summary"),
    ]);
    setQuests(q.data);
    const sync = (cur) => (cur ? q.data.find((x) => x.id === cur.id) || null : null);
    setStepsFor(sync);
    setDetailFor(sync);
    setHistory(h.data);
    setUser(s.data.user);
  }, [setUser]);

  useEffect(() => {
    load();
  }, [load]);

  const openProof = (quest) => {
    setProof({ url: "", kind: "", note: "" });
    setDetailFor(null);
    setProofFor(quest);
  };

  const openSteps = (quest) => {
    setDetailFor(null);
    setStepsFor(quest);
  };

  const start = async (quest) => {
    try {
      const { data } = await api.post(`/quests/${quest.id}/start`);
      if (data.url) window.open(data.url, "_blank", "noopener");
      toast.success(
        quest.tracking === "proof"
          ? "Quest started — upload your proof when you're done."
          : "Quest started — your reward lands automatically once the brand confirms."
      );
      await load();
      if (quest.steps?.length) openSteps({ ...quest, attempt: data.attempt });
      else if (quest.tracking === "proof") openProof(quest);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitProof = async () => {
    if (!proof.url) {
      toast.error("Attach your screenshot or clip first");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/quests/${proofFor.id}/submit`, {
        proof_url: proof.url,
        proof_note: proof.note,
      });
      toast.success("Sent for review — points land once staff approve it.");
      setProofFor(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const earned = history
    .filter((h) => h.status === "approved")
    .reduce((n, h) => n + (h.points_awarded || 0), 0);

  const tabs = useMemo(() => {
    const counts = {};
    quests.forEach((q) => {
      const c = CATEGORIES[q.category] ? q.category : "other";
      counts[c] = (counts[c] || 0) + 1;
    });
    return [["all", "All quests", quests.length],
      ...Object.keys(CATEGORIES)
        .filter((c) => counts[c])
        .map((c) => [c, CATEGORIES[c].label, counts[c]])];
  }, [quests]);

  const visible = quests
    .filter((q) => filter === "all" || (CATEGORIES[q.category] ? q.category : "other") === filter)
    .sort((a, b) => (b.sponsored ? 1 : 0) - (a.sponsored ? 1 : 0) || (b.points || 0) - (a.points || 0));
  const sponsoredCount = visible.filter((q) => q.sponsored).length;

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="mx-auto max-w-7xl px-6 py-8" data-testid="quests-page">
        <div className="flex flex-col gap-5 border-b border-border pb-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-bold sm:text-4xl">Quests</h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Complete tasks to earn points and unlock rewards. Tap any quest for the full brief.
              {sponsoredCount > 0 && (
                <span className="text-foreground/70"> Sponsored quests pay the most — they're first.</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="border border-border bg-card px-5 py-2.5" data-testid="quests-points">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Your points
              </div>
              <div className="mono text-2xl font-semibold text-primary">
                {fmt(user?.points_balance)}
              </div>
            </div>
            <Button asChild className="rounded-none" data-testid="quests-rewards-link">
              <Link to="/rewards">
                <Gift className="mr-2 h-4 w-4" /> Rewards
              </Link>
            </Button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2" data-testid="quest-filters">
          {tabs.map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              data-testid={`quest-filter-${key}`}
              className={`border px-3.5 py-1.5 text-[11px] uppercase tracking-[0.16em] transition-colors ${
                filter === key
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {label} <span className="mono opacity-60">{count}</span>
            </button>
          ))}
        </div>

        {visible.length === 0 && (
          <div className="mt-10 border border-border bg-card p-10 text-center" data-testid="quests-empty">
            <p className="text-sm text-muted-foreground">
              {quests.length === 0
                ? "No live quests right now. New brand drops land here — check back soon."
                : "Nothing in this category yet."}
            </p>
          </div>
        )}

        {visible.length > 0 && (
          <div
            className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="quests-grid"
          >
            {visible.map((q) => (
              <QuestCard key={q.id} q={q} onOpen={setDetailFor} />
            ))}
          </div>
        )}

        {history.length > 0 && (
          <section className="mt-12" data-testid="quest-history">
            <div className="flex items-baseline justify-between">
              <h2 className="text-base font-semibold md:text-lg">My quest activity</h2>
              <span className="text-xs text-muted-foreground">
                <span className="mono text-primary">{fmt(earned)}</span> quest points banked
              </span>
            </div>
            <div className="mt-4 border border-border bg-card">
              {history.map((h) => (
                <div
                  key={h.id}
                  className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-5 py-4 last:border-0"
                  data-testid={`quest-history-${h.id}`}
                >
                  <div>
                    <div className="text-sm font-medium">{h.quest_title}</div>
                    <div className="text-xs text-muted-foreground">
                      {h.period_key !== "once" ? `${h.period_key} · ` : ""}
                      {(h.submitted_at || h.created_at || "").slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="mono text-sm text-primary">
                      {h.status === "approved" ? `+${fmt(h.points_awarded)}` : fmt(h.quest_points)} pts
                    </span>
                    <StatusPill status={h.status} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {detailFor && (
        <QuestDetail
          q={detailFor}
          onClose={() => setDetailFor(null)}
          onStart={start}
          onSteps={openSteps}
          onProof={openProof}
        />
      )}

      {stepsFor && (
        <QuestStepsDialog quest={stepsFor} onClose={() => setStepsFor(null)} onDone={load} />
      )}

      <Dialog open={!!proofFor} onOpenChange={(o) => !o && setProofFor(null)}>
        <DialogContent className="rounded-none border-border bg-card sm:max-w-lg" data-testid="proof-dialog">
          <DialogHeader>
            <DialogTitle>Upload proof — {proofFor?.title}</DialogTitle>
          </DialogHeader>
          {proofFor?.proof_instructions && (
            <p className="text-sm text-muted-foreground">{proofFor.proof_instructions}</p>
          )}
          <ProofField
            media={proofFor?.proof_media || "any"}
            value={proof.url}
            kind={proof.kind}
            onChange={(url, kind) => setProof((p) => ({ ...p, url, kind }))}
            testid="quest-proof-field"
          />
          <Textarea
            className="rounded-none bg-secondary"
            placeholder="Anything staff should know (lap time, order number, username…)"
            value={proof.note}
            onChange={(e) => setProof((p) => ({ ...p, note: e.target.value }))}
            data-testid="proof-note"
          />
          <Button className="rounded-none" disabled={busy} onClick={submitProof} data-testid="proof-submit">
            {busy ? "Sending…" : "Send for review"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
